/**
 * A brand's stores on its own page and on each store's page (web plan W0.10):
 * every store grouped by city, the other stores in a store's city, a store's
 * visible address line, and the offers a store page lists.
 *
 * Pure module (no `window`, no React), so the SEO server (plan W1) and the
 * framework-mode routes (W4) can build the same lists.
 *
 * Cities are grouped exactly like "Where it's valid" on a promotion page
 * (`brandCities`): case-insensitively ("lahore" and "Lahore" are one city),
 * most stores first, then A–Z, and shown as the first spelling typed with a
 * capital letter (else `cityLabel`'s).
 */
import { brandCities } from './promotionRails';
import { cityLabel, cleanAddress, cleanName } from './text';

/**
 * Up to this many stores, the brand page's city groups start open; above it
 * they start closed, one `<h3>{City} ({n})</h3>` per line (the old list showed
 * the first 12 stores before its "Show all" button).
 */
export const STORE_GROUPS_OPEN_MAX = 12;

/**
 * Above this many stores the brand page's rows are LIGHT until their city
 * group is opened: a link with the store's name and address, no thumbnail,
 * buttons or icons. Every store keeps its crawlable `<a href>` in the HTML,
 * but a 434-store brand drops from ~2 MB of markup to a few hundred KB (audit
 * F1: Google reads only the first 2 MB of a page). Opening a group (or
 * in-page find opening it) swaps in the full rows on the client.
 */
export const STORE_ROWS_RICH_MAX = 24;

/** Rows shown before "Show N more" in "Other {Brand} stores in {City}" (the rest stay in the DOM). */
export const CITY_STORES_VISIBLE = 6;

/** What grouping needs from a store. */
export interface StoreLike {
  id: string;
  name?: string | null;
  city?: string | null;
}

/** One city of a brand's stores. */
export interface StoreCityGroup<T> {
  /** Stable key: the city in lower case (`cityKey`); '' for the stores without a city. */
  key: string;
  /** "Lahore"; null for the stores without a city, which come last. */
  city: string | null;
  /** Its stores, A–Z by cleaned name. */
  stores: T[];
}

/** The key two spellings of one city share: `cityLabel` in lower case ('' for none). */
export function cityKey(city: string | null | undefined): string {
  return cityLabel(city).toLowerCase();
}

/** Each store once (by id), null rows skipped, in the given order. */
function uniqueStores<T extends StoreLike>(stores: readonly (T | null | undefined)[] | null | undefined): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const store of stores ?? []) {
    if (!store?.id || seen.has(store.id)) continue;
    seen.add(store.id);
    out.push(store);
  }
  return out;
}

/** A–Z by cleaned name (case- and accent-insensitive), then by id, so the order never depends on the API's. */
function byName<T extends StoreLike>(stores: readonly T[]): T[] {
  return stores
    .map((store) => ({ store, name: cleanName(store.name) || (store.name ?? '') }))
    .sort(
      (a, b) =>
        a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }) ||
        (a.store.id < b.store.id ? -1 : a.store.id > b.store.id ? 1 : 0),
    )
    .map(({ store }) => store);
}

/**
 * Every store once, grouped by city: the brand page's store list. Groups come
 * in `brandCities` order (most stores first, then A–Z); stores without a city
 * form a last group with `city: null`, so no store is ever left out.
 */
export function storesByCity<T extends StoreLike>(
  stores: readonly (T | null | undefined)[] | null | undefined,
): StoreCityGroup<T>[] {
  const list = uniqueStores(stores);
  const byKey = new Map<string, T[]>();
  for (const store of list) {
    const key = cityKey(store.city);
    const group = byKey.get(key);
    if (group) group.push(store);
    else byKey.set(key, [store]);
  }

  const groups: StoreCityGroup<T>[] = brandCities(list).map(({ city }) => {
    const key = cityKey(city);
    return { key, city, stores: byName(byKey.get(key) ?? []) };
  });
  const cityless = byKey.get('');
  if (cityless?.length) groups.push({ key: '', city: null, stores: byName(cityless) });
  return groups;
}

/**
 * The label `city` has among `stores`: the one its group heading shows on the
 * brand page (`brandCities`: the first spelling typed with a capital letter,
 * else `cityLabel`'s). A store typed "dha lahore" reads "DHA Lahore" when a
 * sibling typed it that way. '' for no city.
 */
export function cityLabelAmong(
  stores: readonly ({ city?: string | null } | null | undefined)[] | null | undefined,
  city: string | null | undefined,
): string {
  const key = cityKey(city);
  if (!key) return '';
  const match = brandCities([...(stores ?? []), { city }]).find((c) => cityKey(c.city) === key);
  return match?.city ?? cityLabel(city);
}

/**
 * The brand's other stores in `store`'s city (case-insensitive), A–Z, without
 * `store` itself: "Other {Brand} stores in {City}". None when the store has no
 * city.
 */
export function otherStoresInCity<T extends StoreLike>(
  stores: readonly (T | null | undefined)[] | null | undefined,
  store: StoreLike,
): T[] {
  const key = cityKey(store.city);
  if (!key) return [];
  return byName(uniqueStores(stores).filter((s) => s.id !== store.id && cityKey(s.city) === key));
}

const escapeRegex = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** True when `text` contains `word` as a whole word, ignoring case. */
function hasWord(text: string, word: string): boolean {
  return new RegExp(`(^|[^\\p{L}\\p{N}])${escapeRegex(word)}($|[^\\p{L}\\p{N}])`, 'iu').test(text);
}

/**
 * The address a store page shows: the typed address (`cleanAddress`, so "Shop
 * #F14" keeps its unit number) followed by the city, unless the address
 * already names it ("…, Packages Mall, Lahore" stays as typed). Either part
 * alone when the other is missing; '' when both are.
 */
export function storeAddressLine(store: { address?: string | null; city?: string | null }): string {
  const address = cleanAddress(store.address);
  const city = cityLabel(store.city);
  if (!city) return address;
  if (!address) return city;
  return hasWord(address, city) ? address : `${address}, ${city}`;
}

/**
 * The promotions a store page lists under "Offers from {Brand}": the ones that
 * name the store first, then the rest of its brand's, each once. The brand's
 * are fetched with the store (`store.brand.promotions`) until the backend's
 * `store.activePromotions` replaces them (web plan W3.8).
 */
export function storeOffers<T extends { id: string }>(
  own: readonly (T | null | undefined)[] | null | undefined,
  brand: readonly (T | null | undefined)[] | null | undefined,
): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const promo of [...(own ?? []), ...(brand ?? [])]) {
    if (!promo?.id || seen.has(promo.id)) continue;
    seen.add(promo.id);
    out.push(promo);
  }
  return out;
}
